import { Component } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { CloseButton, BackButton } from '../ui/navButtons';
import { CoinIcon, COIN_ICON_COLOR } from '../ui/icons';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { askConfirm } from '../../utils/confirmBus';
import { getClass, getNode } from '../../utils/arena/classes';
import {
  TOTAL_SKILL_POINTS, SKILL_RESET_COST,
  getNodeStatus, getNodeBlocker, countSpent, spendPoint, purchaseSkillReset, getSkillState,
} from '../../utils/arena/skills';
import { t } from '../../localization/t';
import AlchemistPotency from './AlchemistPotency';
import OracleForesight from './OracleForesight';
import BattlemageStanding from './BattlemageStanding';
import Constellation from './Constellation';
import { foldChoices, isChoiceKey, choiceStatus, chosenOption } from './choiceNodes';
import { positionTooltip, TOOLTIP_H_EST, getCanvasScaleNow } from './positionTooltip';
import { potionArt, potionArtMask } from '../../utils/arena/alchemist';

// Region captions name the branches; humours also carry their Sorcery element.
const REGIONS = {
  alchemist: () => [
    { label: t('Pituita'), element: 'Water', x: 290, y: 75 },
    { label: t('Sanguis'), element: 'Air', x: 710, y: 75 },
    { label: t('Melancholia'), element: 'Earth', x: 75, y: 530 },
    { label: t('Cholera'), element: 'Fire', x: 925, y: 530 },
  ],
  wildheart: () => [
    { label: t('The Hunt'), x: 230, y: 590 },
    { label: t('The Altars'), x: 500, y: 20 },
    { label: t('Survival'), x: 790, y: 590 },
  ],
};

function OptionBlock({ cls, node, pinned, confirming, onLearn }) {
  return (
    <div className="vh-class-tooltip-option">
      <h3 className="vh-skill-tooltip-title arena-heading">{t(node.title)}</h3>
      <p className="vh-skill-tooltip-body">{t(node.desc)}</p>
      {pinned ? (
        <button
          type="button"
          className="vh-skill-tooltip-btn vh-skill-tooltip-btn-confirm vh-class-tooltip-option-btn"
          onClick={() => onLearn(node.key)}
          disabled={confirming}
          data-testid={`skill-confirm-${node.key}`}
        >
          {t('Learn')}
        </button>
      ) : null}
    </div>
  );
}

// A potion tooltip's bottle hangs past the panel's top right corner by this
// much (cqw); the placement counts it so the bottle never leaves the canvas.
const BOTTLE_OVERHANG = { x: 2, y: 2.5 };

function NodeTooltip({ cls, node, status, blocker, anchorRect, pinned, confirming, onMeasure, onLearn }) {
  const ref = useRef(null);
  // 1cqw in px: the canvas is 1600 design px wide.
  const cqw = 16 * getCanvasScaleNow();
  const over = node.potion ? { x: BOTTLE_OVERHANG.x * cqw, y: BOTTLE_OVERHANG.y * cqw } : { x: 0, y: 0 };
  // A pinned tooltip keeps room under itself for the confirm bar, so the
  // pair is placed as one block and neither leaves the canvas.
  const reserve = pinned ? (CONFIRM_BAR_FALLBACK_H + CONFIRM_BAR_GAP) * getCanvasScaleNow() : 0;
  const [pos, setPos] = useState(() => positionTooltip(anchorRect, TOOLTIP_H_EST * getCanvasScaleNow() + over.y + reserve));

  useLayoutEffect(() => {
    if (!anchorRect || !ref.current) return;
    // Layout size, not the bounding rect: the panel is still in its entry
    // transform (scale 0.96) when this runs, and the bar under it must be
    // cut to the panel's resting width.
    const el = ref.current;
    const rect = el.getBoundingClientRect();
    const h = el.offsetHeight || rect.height;
    const w = el.offsetWidth || rect.width;
    if (h <= 0) return;
    const next = positionTooltip(anchorRect, h + over.y + reserve, w + over.x);
    setPos((prev) => (prev && next && prev.left === next.left && prev.top === next.top ? prev : next));
    onMeasure?.(h + over.y + reserve, w + over.x, over.x);
  }, [anchorRect, node.key, confirming, onMeasure, over.x, over.y, reserve]);

  if (!pos) return null;
  const learned = status === 'purchased';
  // On a potion tooltip the bottle owns the corner, so the badge sits inline
  // beside the kicker; every other tooltip keeps it in the corner.
  const inline = Boolean(node.potion);
  const badgeStyle = inline ? { position: 'static', display: 'inline-flex', alignItems: 'center', marginLeft: '0.5cqw', verticalAlign: 'middle' } : {};
  const badge = !inline && (blocker || learned) ? ' vh-skill-tooltip-with-badge' : '';
  const badgeNode = blocker
    ? <span className="vh-skill-tooltip-lock-badge" style={badgeStyle}>{t('Not available')}</span>
    : learned
      ? (
        <span className="vh-skill-tooltip-lock-badge" data-testid="tooltip-learned" style={{
          ...badgeStyle,
          color: cls.accent,
          background: 'linear-gradient(180deg, #2a2133 0%, #1b1521 100%)',
          boxShadow: `inset 0 0.0625cqw 0 rgba(255,255,255,0.08), inset 0 -0.0625cqw 0 rgba(0,0,0,0.3), 0 0 0 0.0625cqw ${cls.accent}99, 0 0 0.375cqw ${cls.accent}44`,
        }}>{t('Learned')}</span>
      )
      : null;
  const kicker = node.potion ? (node.unlock === 'recipe' ? t('Recipe') : t('Potion drop')) : null;

  return (
    <div style={{ position: 'fixed', left: pos.left, top: pos.top + over.y, pointerEvents: pinned ? 'auto' : 'none', zIndex: 200 }}>
      {/* A potion node's bottle, large over the top right corner and past
          the border, the strokes fading where they leave the panel. Kept
          outside the panel, which clips its own children. */}
      {node.potion ? (
        <motion.img
          src={potionArt(node.potion)} alt="" draggable={false} aria-hidden="true" data-testid="tooltip-potion"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.16, ease: [0.22, 0.8, 0.36, 1] }}
          style={{
            // Above the panel, which carries its own z-index from the stylesheet.
            position: 'absolute', right: '-2cqw', top: '-2.5cqw', width: '9.5cqw', height: '9.5cqw', objectFit: 'contain', pointerEvents: 'none', zIndex: 201,
            // A hint of fade toward the outer corner, no more.
            maskImage: `${potionArtMask(node.potion)}, linear-gradient(225deg, rgba(0,0,0,0.6) 0%, #000 42%)`,
            maskSize: '100% 100%, 100% 100%', maskComposite: 'intersect',
            filter: 'drop-shadow(0 0.25cqw 0.75cqw rgba(0,0,0,0.7))',
          }}
        />
      ) : null}
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.96, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 4 }}
        transition={{ duration: 0.16, ease: [0.22, 0.8, 0.36, 1] }}
        className={`vh-skill-tooltip vh-class-tooltip${pinned ? ' vh-skill-tooltip-pinned' : ''}${confirming ? ' vh-skill-tooltip-confirming' : ''}`}
        style={{
          position: 'relative',
          // A potion node's tooltip is wider so the copy keeps its measure
          // beside the bottle instead of wrapping under it.
          ...(node.potion ? { width: '25cqw' } : null),
          '--vh-tooltip-accent': cls.accent,
          '--vh-tooltip-accent-glow': `${cls.accent}b3`,
          '--vh-tooltip-glow': `${cls.accent}33`,
          // Inline because Bun's CSS minifier strips url() from .css files.
          '--vh-tooltip-medallion-svg': `url("${cls.majorOrnament || '/ornaments/path-hunter.svg'}")`,
        }}
      >
        {/* The class ornament bleeding in from the left edge, fading
            into the panel. Same treatment the old paths had. */}
        <div className="vh-skill-tooltip-medallion" aria-hidden="true" />
        {confirming ? <div className="vh-skill-tooltip-sweep" aria-hidden="true" /> : null}
        <div className="vh-skill-tooltip-accent" />
        {inline ? null : badgeNode}
        {node.choice ? (
          <>
            <div className={`vh-skill-tooltip-path${badge}`} style={{ color: cls.accent }}>
              {t('Choose one')}
            </div>
            <div className="vh-class-tooltip-options">
              <OptionBlock cls={cls} node={node.options[0]} pinned={pinned} confirming={confirming} onLearn={onLearn} />
              <div className="vh-class-tooltip-or" aria-hidden="true"><span>{t('or')}</span></div>
              <OptionBlock cls={cls} node={node.options[1]} pinned={pinned} confirming={confirming} onLearn={onLearn} />
            </div>
          </>
        ) : (
          <>
            {/* A potion node's kicker row: what kind of node, its badge beside it. */}
            {kicker ? (
              <div className="vh-skill-tooltip-path" style={{ color: cls.accent, display: 'flex', alignItems: 'center' }}>
                {kicker}{badgeNode}
              </div>
            ) : null}
            <div className={`vh-skill-tooltip-title-row${badge}`} style={inline ? { paddingRight: '6cqw' } : undefined}>
              <h3 className="vh-skill-tooltip-title arena-heading">{t(node.title)}</h3>
            </div>
            <div className="vh-skill-tooltip-divider" />
            <p className="vh-skill-tooltip-body" style={node.potion ? { paddingRight: '3.5cqw' } : undefined}>{t(node.desc)}</p>
            {node.effect ? <p className="vh-skill-tooltip-body vh-potion-effect">{t(node.effect)}</p> : null}
            {node.flavour ? <p className="vh-potion-flavour" style={{ color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}>{t('“{text}”', { text: t(node.flavour) })}</p> : null}
          </>
        )}
        {learned && node.chosenOver ? (
          <p className="vh-class-tooltip-note" style={{ color: cls.accent }}>
            {t('Chosen over {name}', { name: t(node.chosenOver.title) })}
          </p>
        ) : null}
      </motion.div>
    </div>
  );
}

const CONFIRM_BAR_GAP = 10;
const CONFIRM_BAR_FALLBACK_H = 38;
const TOOLTIP_W_FALLBACK = 320; // 20cqw on the 1600 design canvas

function ConfirmBar({ accent, anchorRect, confirming, onConfirm, onCancel, tooltipHeight, tooltipWidth, tooltipInset = 0, choice }) {
  const barRef = useRef(null);
  const scale = getCanvasScaleNow();
  // The same block the tooltip was placed by: its height already holds the
  // bar's room, so the bar lands under the panel at the block's foot.
  const total = tooltipHeight ?? (TOOLTIP_H_EST + CONFIRM_BAR_FALLBACK_H + CONFIRM_BAR_GAP) * scale;
  const width = tooltipWidth ?? TOOLTIP_W_FALLBACK * scale;
  const pos = positionTooltip(anchorRect, total, width);
  const [barHeight, setBarHeight] = useState(CONFIRM_BAR_FALLBACK_H * scale);

  useLayoutEffect(() => {
    if (!barRef.current) return;
    const h = barRef.current.offsetHeight || barRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - barHeight) > 0.5) setBarHeight(h);
  }, [anchorRect, confirming, barHeight]);

  if (!pos) return null;
  return (
    <div style={{ position: 'fixed', left: pos.left, top: pos.top + total - barHeight, zIndex: 201 }}>
      <motion.div
        ref={barRef}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        transition={{ duration: 0.14, ease: [0.22, 0.8, 0.36, 1] }}
        className="vh-skill-tooltip-confirm-bar"
        style={{ position: 'relative', width: width - tooltipInset, '--vh-tooltip-accent': accent, '--vh-tooltip-accent-glow': `${accent}b3` }}
      >
        <button type="button" className="vh-skill-tooltip-btn vh-skill-tooltip-btn-cancel" onClick={onCancel} disabled={confirming}>
          {t('Cancel')}
        </button>
        {!choice ? (
          <button type="button" className="vh-skill-tooltip-btn vh-skill-tooltip-btn-confirm" onClick={onConfirm} disabled={confirming} data-testid="skill-confirm">
            {t('Learn')}
          </button>
        ) : null}
      </motion.div>
    </div>
  );
}

// The number a player acts on is the points they can spend now, so that
// leads. The track beneath shows the lifetime budget: spent, open, unearned.
function PointsHud({ spent, remaining, availablePoints }) {
  return (
    <div className="vh-skill-points-hud" data-tutorial="skill-points-hud">
      <span
        className={`arena-heading font-bold tabular-nums vh-skill-points-hud-value${remaining > 0 ? ' vh-skill-points-hud-value-live' : ''}`}
      >
        {remaining}
      </span>
      <div className="vh-skill-points-hud-meta">
        <div className="vh-skill-points-hud-row">
          <span className="vh-skill-points-hud-label">{remaining === 1 ? t('Point to spend') : t('Points to spend')}</span>
          <span className="vh-skill-points-hud-meta-sub">{t('{spent} / {total} earned', { spent: availablePoints, total: TOTAL_SKILL_POINTS })}</span>
        </div>
        <div className="vh-skill-points-hud-track" aria-hidden="true">
          {Array.from({ length: TOTAL_SKILL_POINTS }).map((_, i) => (
            <span key={i} className={`vh-skill-seg ${i < spent ? 'vh-skill-seg-spent' : i < availablePoints ? 'vh-skill-seg-open' : ''}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Every path that closes the pinned tooltip drops its measured height
// with it, so the next pin measures fresh.
const NO_PENDING = { pending: null, pinnedTooltipHeight: null, pinnedTooltipWidth: null };

// The chosen class's tree, drawn as its constellation. Hover a star to
// read it, click an available star to pin it and confirm the point.
// In read-only mode (viewing another player) nothing can be spent.
export default class ClassTreeScreen extends Component {
  state = {
    hoveredKey: null,
    hoverRect: null,
    pending: null, // { key, anchorRect, confirming }
    pinnedTooltipHeight: null,
    pinnedTooltipWidth: null,
    flareKey: null,
    resetting: false,
  };

  componentWillUnmount() {
    this._unmounted = true;
    if (this._confirmTimer) clearTimeout(this._confirmTimer);
    if (this._flareTimer) clearTimeout(this._flareTimer);
  }

  get cls() { return getClass(this.props.skillState.classKey); }

  get choices() {
    const cls = this.cls;
    if (!cls) return null;
    if (this._choicesFor !== cls.key) {
      this._choicesFor = cls.key;
      this._choices = foldChoices(cls.nodes).groups;
    }
    return this._choices;
  }

  realStatusOf = (key) => getNodeStatus(key, this.props.skillState, this.props.effectiveLevel);

  statusOf = (key) => {
    if (isChoiceKey(key)) {
      const group = this.choices?.get(key);
      return group ? choiceStatus(group, this.realStatusOf) : 'locked';
    }
    return this.realStatusOf(key);
  };

  // What the tooltip describes for a key: a plain node, or a choice node
  // carrying both options and, once taken, the chosen one.
  tooltipNodeFor(key) {
    if (!key) return null;
    if (!isChoiceKey(key)) return getNode(this.cls.key, key);
    const group = this.choices?.get(key);
    if (!group) return null;
    const chosen = chosenOption(group, this.realStatusOf);
    if (chosen) {
      const other = group.options.find((o) => o.key !== chosen.key);
      return { ...chosen, chosenOver: other };
    }
    return group;
  }

  handlePinnedTooltipMeasure = (h, w, inset = 0) => {
    if (!h || h <= 0) return;
    const { pinnedTooltipHeight: ph, pinnedTooltipWidth: pw } = this.state;
    if (ph != null && Math.abs(ph - h) < 0.5 && pw != null && Math.abs(pw - w) < 0.5) return;
    this.setState({ pinnedTooltipHeight: h, pinnedTooltipWidth: w, pinnedTooltipInset: inset });
  };

  handleHover = (key, e) => {
    if (!key) { this.setState({ hoveredKey: null, hoverRect: null }); return; }
    if (key && key !== this.state.hoveredKey) playUI(UI.HOVER, { volume: 0.4 });
    this.setState({ hoveredKey: key, hoverRect: e?.currentTarget?.getBoundingClientRect?.() || null });
  };

  handleClick = (key, e) => {
    if (this.props.readOnly) return;
    if (this.statusOf(key) !== 'available') { playUI(UI.CLICK_SOFT); return; }
    if (countSpent(this.props.skillState) >= this.props.availablePoints) {
      playUI(UI.CLICK_SOFT);
      toast(t('No skill points left to spend.'));
      return;
    }
    playUI(UI.CLICK);
    this.setState({
      pending: { key, anchorRect: e?.currentTarget?.getBoundingClientRect?.() || null, confirming: false },
      hoveredKey: null,
      pinnedTooltipHeight: null,
      pinnedTooltipWidth: null,
    });
  };

  handleCancel = () => {
    if (this.state.pending?.confirming) return;
    playUI(UI.CLICK_SOFT);
    this.setState(NO_PENDING);
  };

  handleConfirm = (optionKey) => {
    const { pending } = this.state;
    if (!pending || pending.confirming || this.props.readOnly) return;
    // A choice node spends on the option the player picked; a plain node
    // spends on itself.
    const key = isChoiceKey(pending.key) ? optionKey : pending.key;
    if (!key || this.realStatusOf(key) !== 'available' || countSpent(this.props.skillState) >= this.props.availablePoints) {
      this.setState(NO_PENDING);
      return;
    }
    playUI(UI.CONFIRM);
    this.setState({ pending: { ...pending, confirming: true } });
    if (this._confirmTimer) clearTimeout(this._confirmTimer);
    const flareKey = pending.key;
    const sweep = new Promise((r) => { this._confirmTimer = setTimeout(r, 700); });
    Promise.all([sweep, spendPoint(key)])
      .then(([, next]) => {
        if (this._unmounted) return;
        playUI(UI.UPGRADE);
        this.props.onSkillState(next);
        this.setState({ ...NO_PENDING, flareKey });
        if (this._flareTimer) clearTimeout(this._flareTimer);
        this._flareTimer = setTimeout(() => { if (!this._unmounted) this.setState({ flareKey: null }); }, 900);
      })
      .catch((err) => {
        console.error('Failed to spend skill point:', err);
        if (this._unmounted) return;
        this.props.onSkillState(getSkillState());
        this.setState(NO_PENDING);
        toast.error(t('That point did not take. Try again.'));
      });
  };

  handleReset = async () => {
    if (this.props.readOnly || this.state.resetting) return;
    const coins = this.props.profile?.coins || 0;
    if (coins < SKILL_RESET_COST) {
      toast.error(t('You need {n} gold to change your class.', { n: SKILL_RESET_COST }));
      return;
    }
    const ok = await askConfirm({
      title: t('Renounce the {name}?', { name: t(this.cls.title) }),
      message: t('This refunds every spent point and lets you choose a class again, for {n} gold. You keep all the points you have earned.', { n: SKILL_RESET_COST }),
      confirmLabel: (
        <span className="inline-flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 tabular-nums" style={{ color: COIN_ICON_COLOR }}>
            <CoinIcon size={13} glow={false} />
            {SKILL_RESET_COST}
          </span>
          <span aria-hidden="true" className="self-stretch w-px" style={{ background: 'currentColor', opacity: 0.3 }} />
          <span>{t('Renounce')}</span>
        </span>
      ),
      cancelLabel: t('Cancel'),
      danger: true,
    });
    if (!ok) return;
    this.setState({ resetting: true });
    try {
      const { skills, coins } = await purchaseSkillReset();
      playUI(UI.GOLD);
      if (!this._unmounted) this.setState({ ...NO_PENDING, resetting: false, hoveredKey: null });
      this.props.onSkillState(skills);
      if (typeof coins === 'number') this.props.onCoins?.(coins);
      toast.success(t('Your points are yours again. Choose a class.'));
    } catch (err) {
      if (!this._unmounted) this.setState({ resetting: false });
      toast.error(err?.status === 402
        ? t('You need {n} gold to change your class.', { n: SKILL_RESET_COST })
        : t('Failed to change your class. Try again.'));
    }
  };

  render() {
    const { skillState, availablePoints, readOnly, onClose, profile, viewName, onBack } = this.props;
    const { hoveredKey, hoverRect, pending, pinnedTooltipHeight, pinnedTooltipWidth, flareKey, resetting } = this.state;
    const cls = this.cls;
    if (!cls) return null;
    const spent = countSpent(skillState);
    const remaining = Math.max(0, availablePoints - spent);
    const canAffordReset = (profile?.coins || 0) >= SKILL_RESET_COST;
    const tooltipKey = pending?.key || hoveredKey;
    const tooltipNode = this.tooltipNodeFor(tooltipKey);
    // A choice node's options share prerequisites, so either one explains the lock.
    const blockerKey = tooltipNode?.choice ? tooltipNode.options[0].key : tooltipKey;
    const regions = REGIONS[cls.key]?.() || null;

    return (
      <div className="absolute inset-0 flex flex-col" style={{ '--vh-class-accent': cls.accent }} data-testid="class-tree">
        <div className="relative shrink-0 flex items-start justify-between px-12 pt-10">
          <div className="flex items-center" style={{ gap: '1cqw' }}>
            {onBack ? <BackButton title={t('Back to the classes')} onClick={onBack} testId="class-back" /> : null}
            <div>
              <div className="arena-heading font-bold" style={{ color: cls.accent, fontSize: '2cqw', textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                {viewName ? t("{name}'s {class}", { name: viewName, class: t(cls.title) }) : t(cls.title)}
              </div>
              <div className="vh-class-sign-tagline" style={{ fontSize: '0.8125cqw' }}>{t(cls.tagline)}</div>
            </div>
          </div>
          <div className="flex items-center" style={{ gap: '1.25cqw' }}>
            {!readOnly ? <PointsHud spent={spent} remaining={remaining} availablePoints={availablePoints} /> : null}
            <CloseButton title={t('Close')} onClick={onClose} />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className={`relative pointer-events-none vh-const-labelled${cls.key === 'alchemist' ? ' vh-alchemist-constellation' : ''}`} data-humours-tree={cls.key === 'alchemist' ? '' : undefined} style={{ width: '92.4cqw' }}>
            <Constellation
              enter
              nodes={cls.nodes}
              showLabels
              accent={cls.accent}
              ornament={cls.ornament}
              majorOrnament={cls.majorOrnament}
              statusOf={this.statusOf}
              hoveredKey={hoveredKey}
              pinnedKey={pending?.key || null}
              flareKey={flareKey}
              onHover={this.handleHover}
              onClick={this.handleClick}
              regions={regions}
            />
          </div>
        </div>

        {cls.key === 'alchemist' ? <AlchemistPotency skillState={skillState} /> : null}
        {cls.key === 'oracle' && !readOnly ? <OracleForesight skillState={skillState} oracle={this.props.oracle} /> : null}
        {cls.key === 'battlemage' && !readOnly ? <BattlemageStanding skillState={skillState} battlemage={this.props.battlemage} /> : null}

        {!readOnly ? (
          <div className="vh-class-tree-footer shrink-0 flex items-center justify-between px-12 pb-8">
            <button
              type="button"
              onClick={this.handleReset}
              disabled={resetting}
              className="inline-flex items-center gap-3 vh-skill-action-btn vh-skill-action-btn-danger"
              style={canAffordReset ? null : { opacity: 0.45, cursor: 'url("/cursors/default.png") 4 2, not-allowed' }}
              title={canAffordReset
                ? t('Refund every spent point and choose a class again for {n} gold', { n: SKILL_RESET_COST })
                : t('You need {n} gold to change your class', { n: SKILL_RESET_COST })}
              data-testid="class-reset"
            >
              {resetting ? t('Renouncing…') : (
                <>
                  <span className="inline-flex items-center gap-1.5 font-bold tabular-nums" style={{ color: COIN_ICON_COLOR }}>
                    <CoinIcon size={13} />
                    {SKILL_RESET_COST}
                  </span>
                  <span aria-hidden="true" className="self-stretch w-px" style={{ background: 'rgba(232,195,110,0.22)' }} />
                  <span>{t('Change Class')}</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="shrink-0 pb-8" />
        )}

        {pending && !pending.confirming ? (
          <div className="fixed inset-0 z-[105]" style={{ background: 'transparent' }} onClick={this.handleCancel} />
        ) : null}

        <AnimatePresence>
          {tooltipNode ? (
            <NodeTooltip
              key={pending ? `pinned-${pending.key}` : tooltipKey}
              cls={cls}
              node={tooltipNode}
              status={this.statusOf(tooltipKey)}
              blocker={getNodeBlocker(blockerKey, skillState, this.props.effectiveLevel)}
              anchorRect={pending ? pending.anchorRect : hoverRect}
              pinned={Boolean(pending)}
              confirming={Boolean(pending?.confirming)}
              onMeasure={pending ? this.handlePinnedTooltipMeasure : undefined}
              onLearn={this.handleConfirm}
            />
          ) : null}
        </AnimatePresence>
        <AnimatePresence>
          {pending ? (
            <ConfirmBar
              accent={cls.accent}
              anchorRect={pending.anchorRect}
              confirming={pending.confirming}
              onConfirm={() => this.handleConfirm()}
              onCancel={this.handleCancel}
              tooltipHeight={pinnedTooltipHeight}
              tooltipWidth={pinnedTooltipWidth}
              tooltipInset={this.state.pinnedTooltipInset || 0}
              choice={isChoiceKey(pending.key)}
            />
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}
